import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, FolderOpen, Plus, Search } from 'lucide-react';
import { Button } from 'components/ui/button';
import { PageHeader } from 'components/ui/page-header';
import { SegmentedTabs, TableSearch } from 'components/ui/data-table';
import { LoadMore } from 'components/ui/pagination';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import * as CollectionsModule from 'modules/collections';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useIsMobile } from 'hooks/use-media-query';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { LibraryMobile } from 'components/mobile/screens/LibraryMobile';
import { CollectionFormModal } from './CollectionFormModal';
import { CollectionCard } from './CollectionCard';
import { CreateTile } from './CreateTile';
import { type LibraryFolder } from './LibraryFolderTree';

/** Collections revealed per "show more" — both lists arrive whole (the API isn't paged). */
const STEP = 12;
const GRID = 'grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4';

/** Owned vs shared-with-me is a segmented view. Share-state is best-effort: owned reads "Private", shared "Group". */
const LibraryDesktop: React.FC = () => {
  const { t } = useTranslation();
  const [folder, setFolder] = useState<LibraryFolder>('all');
  const { input: search, setInput: setSearch } = useSearchParamState();
  const [shown, setShown] = useState(STEP);
  const [modalOpen, setModalOpen] = useState(false);

  const canCreate = usePermission(PERMISSIONS.COLLECTIONS_CREATE);

  const owned = CollectionsModule.Hooks.useOwned();
  const shared = CollectionsModule.Hooks.useSharedWithMe();
  const current = folder === 'all' ? owned : shared;
  const { collections, isLoading, isError, refetch } = current;

  const needle = search.trim().toLocaleLowerCase();
  const filtered = useMemo(
    () => (needle ? collections.filter((c) => c.name.toLocaleLowerCase().includes(needle)) : collections),
    [collections, needle],
  );

  // A new view or query starts again from the first batch.
  const changeFolder = (next: LibraryFolder) => {
    setFolder(next);
    setShown(STEP);
  };
  const changeSearch = (value: string) => {
    setSearch(value);
    setShown(STEP);
  };

  const openCreate = () => setModalOpen(true);
  const showCreateTile = folder === 'all' && canCreate && !needle;

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={GRID} aria-hidden>
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-[148px] rounded-lg" />
          ))}
        </div>
      );
    }
    if (isError) {
      return (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      );
    }
    if (collections.length === 0) {
      return (
        <EmptyState
          icon={<FolderOpen size={28} />}
          title={t('library.no_collections_title')}
          description={folder === 'shared' ? t('library.nothing_shared_desc') : undefined}
          action={
            folder === 'all' && canCreate
              ? { label: t('library.new_collection'), icon: <Plus className="h-4 w-4" />, onClick: openCreate }
              : undefined
          }
        />
      );
    }
    if (filtered.length === 0) {
      return (
        <EmptyState
          icon={<Search size={28} />}
          title={t('collections.none_match')}
          description={t('try_adjusting_your_search_or_filters')}
          secondaryAction={{ label: t('clear_search'), onClick: () => changeSearch('') }}
        />
      );
    }
    return (
      <div className="flex flex-col gap-6">
        <div className={GRID}>
          {showCreateTile && <CreateTile label={t('library.new_collection')} onClick={openCreate} />}
          {filtered.slice(0, shown).map((c) => (
            <CollectionCard
              key={c.id}
              name={c.name}
              itemCount={c.itemCount}
              updatedAt={c.updatedAt}
              shareState={folder === 'shared' ? 'team' : 'private'}
              to={`/app/collections/${c.id}`}
            />
          ))}
        </div>
        <LoadMore
          shown={Math.min(shown, filtered.length)}
          total={filtered.length}
          step={STEP}
          onLoadMore={() => setShown((n) => n + STEP)}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('library.title')}
        description={t('library.subtitle')}
        actions={
          canCreate && (
            <Button size="sm" className="gap-1.5" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('library.new_collection')}
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <SegmentedTabs
          aria-label={t('library.title')}
          value={folder}
          onChange={changeFolder}
          options={[
            { value: 'all', label: t('library.all_collections'), count: owned.isLoading ? undefined : owned.collections.length },
            { value: 'shared', label: t('library.shared_with_me'), count: shared.isLoading ? undefined : shared.collections.length },
          ]}
        />
        <TableSearch
          value={search}
          onChange={changeSearch}
          placeholder={t('collections.search_collections')}
          aria-label={t('collections.search_collections')}
          className="ml-auto"
        />
      </div>

      {renderBody()}

      <CollectionFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        collection={null}
        onSuccess={() => setModalOpen(false)}
      />
    </div>
  );
};

// Phones render the dedicated mobile collection list; thin wrapper keeps hook
// count constant across the breakpoint.
const LibraryPage: React.FC = () => (useIsMobile() ? <LibraryMobile /> : <LibraryDesktop />);

export default LibraryPage;
